import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  updateDocById,
  uploadFile as firebaseUploadFile,
} from "../../../utils/firebase/Firebase.utils";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../../../utils/firebase/Firebase.utils";
import { v4 as uuidv4 } from "uuid";
import FileUploadAndView from "../../User/Components/FileUploadAndView";
import {
  FiTrash2,
  FiAlertCircle,
  FiMaximize2,
  FiX,
  FiUpload,
  FiPaperclip,
  FiImage,
  FiVideo,
} from "react-icons/fi";

function EmergencyModal({ emergency, unitNumber, onClose, onResolve, onEdit }) {
  const currentUser = useSelector((state) => state.userObject);
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmergency, setEditedEmergency] = useState({});
  const [newAction, setNewAction] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [activeUpload, setActiveUpload] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [tempMedia, setTempMedia] = useState([]); // Debug log

  // Update edited emergency when source emergency changes
  useEffect(() => {
    if (emergency) { // Debug log
      setEditedEmergency({ ...emergency });
      setTempMedia(emergency.media || []);
    }
  }, [emergency]);

  // Early return after all hooks have been initialized
  if (!emergency) return null;

  const handleUpload = async (event, type) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const currentMedia = tempMedia || [];
    if (currentMedia.length >= 10) {
      alert(`You can only upload up to 10 files`);
      return;
    }

    const remainingSlots = 10 - currentMedia.length;
    const filesToProcess = files.slice(0, remainingSlots);

    setActiveUpload(type);
    setUploadProgress(0);

    try {
      // Upload files to Firebase Storage
      const newFiles = await Promise.all(
        filesToProcess.map(async (file) => {
          // Create a unique filename to prevent collisions
          const fileName = `emergencies/${emergency.id}/${uuidv4()}-${
            file.name
          }`;
          const storageRef = ref(storage, fileName);

          // Upload file with progress monitoring
          const uploadTask = uploadBytesResumable(storageRef, file);

          return new Promise((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                // Calculate and update progress
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                // Handle errors
                console.error("Upload error:", error);
                reject(error);
              },
              async () => {
                // Upload completed - get download URL
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );

                // Return file metadata with permanent URL
                resolve({
                  url: downloadURL,
                  name: file.name,
                  type: file.type.startsWith("image/") ? "image" : "video",
                  timestamp: new Date().toISOString(),
                  uploadedBy: currentUser.displayName || currentUser.email,
                  uploadedById: currentUser.uid,
                  storagePath: fileName, // Store path for future deletion if needed
                });
              }
            );
          });
        })
      );

      // Update local state with new media first
      const updatedMedia = [...currentMedia, ...newFiles];
      setTempMedia(updatedMedia);

      // Update Firestore with accumulated media
      const updatedEmergency = {
        ...emergency,
        media: updatedMedia,
      }; // Debug log

      await updateDocById("emergencies", emergency.id, updatedEmergency);
      onEdit(updatedEmergency);
      setActiveUpload("");
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files");
      setActiveUpload("");
    }
  };

  const handleDeleteMedia = async (mediaIndex) => {
    try {
      const mediaToDelete = tempMedia[mediaIndex];
      const updatedMedia = tempMedia.filter((_, index) => index !== mediaIndex);

      // Update local state
      setTempMedia(updatedMedia);

      // If the media has a storagePath, attempt to delete from Firebase Storage
      if (mediaToDelete && mediaToDelete.storagePath) {
        try {
          const fileRef = ref(storage, mediaToDelete.storagePath);
          await deleteObject(fileRef);
        } catch (storageError) {
          // Don't stop the process if storage deletion fails
          console.error("Error deleting file from storage:", storageError);
        }
      }

      // Update Firestore
      const updatedEmergency = {
        ...emergency,
        media: updatedMedia,
      }; // Debug log

      await updateDocById("emergencies", emergency.id, updatedEmergency);
      onEdit(updatedEmergency);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting media:", error);
      alert("Failed to delete media");
    }
  };

  const addNewAction = async () => {
    if (!newAction.trim()) return;

    try {
      const actionItem = {
        action: newAction.trim(),
        addedBy: currentUser.displayName || currentUser.email,
        addedById: currentUser.uid,
        timestamp: new Date().toISOString(),
        media: [], // Action should have its own media, not the emergency media
      };

      const updatedEmergency = {
        ...emergency,
        actions: [...(emergency.actions || []), actionItem],
      };

      await updateDocById("emergencies", emergency.id, updatedEmergency);
      onEdit(updatedEmergency);
      setNewAction("");
    } catch (error) {
      console.error("Error adding action:", error);
      alert("Failed to add action");
    }
  };

  const openMediaPreview = (media) => {
    setSelectedMedia(media);
  };

  const closeMediaPreview = () => {
    setSelectedMedia(null);
  };

  const priorityColors = {
    High: "bg-red-600",
    Medium: "bg-orange-500",
    Low: "bg-yellow-500",
  };

  const handleEditSave = async () => {
    try {
      await updateDocById("emergencies", emergency.id, editedEmergency);
      onEdit(editedEmergency);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating emergency:", error);
      alert("Failed to update emergency");
    }
  };

  const resolveEmergency = async () => {
    try {
      if (!resolutionNotes.trim()) {
        alert("Please provide resolution notes");
        return;
      }

      const resolvedEmergency = {
        ...emergency,
        status: "Resolved",
        resolvedAt: new Date().toISOString(),
        resolutionNotes: resolutionNotes,
        resolvedBy: currentUser.displayName || currentUser.email,
        resolvedById: currentUser.uid,
      };

      await updateDocById("emergencies", emergency.id, resolvedEmergency);
      onResolve();
    } catch (error) {
      console.error("Error resolving emergency:", error);
      alert("Failed to resolve emergency");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ color: "#363062" }}>
                  Emergency: Unit {unitNumber}
                </h2>
                <p className="text-sm text-gray-500">
                  {new Date(
                    emergency.timestamp || emergency.createdAt
                  ).toLocaleString()}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span
                  className={`px-2 py-1 text-xs font-semibold text-white rounded-full ${
                    priorityColors[emergency.priority]
                  }`}
                >
                  {emergency.priority} Priority
                </span>
                <span
                  className={`px-2 py-1 text-xs font-semibold text-white rounded-full ${
                    emergency.status === "Active"
                      ? "bg-[#8FCD00]"
                      : "bg-[#4299E1]"
                  }`}
                >
                  {emergency.status}
                </span>
              </div>

              {isEditing ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      value={editedEmergency.type}
                      onChange={(e) =>
                        setEditedEmergency({
                          ...editedEmergency,
                          type: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="Fire Alarm">Fire Alarm</option>
                      <option value="Water Leak">Water Leak</option>
                      <option value="Medical Emergency">
                        Medical Emergency
                      </option>
                      <option value="Security Breach">Security Breach</option>
                      <option value="Elevator Malfunction">
                        Elevator Malfunction
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Priority
                    </label>
                    <select
                      value={editedEmergency.priority}
                      onChange={(e) =>
                        setEditedEmergency({
                          ...editedEmergency,
                          priority: e.target.value,
                        })
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Details
                    </label>
                    <textarea
                      value={editedEmergency.details}
                      onChange={(e) =>
                        setEditedEmergency({
                          ...editedEmergency,
                          details: e.target.value,
                        })
                      }
                      rows={3}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="font-semibold text-gray-900">Type</h3>
                    <p className="text-gray-700">{emergency.type}</p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900">Details</h3>
                    <p className="text-gray-700">{emergency.details}</p>
                  </div>
                </>
              )}

              {/* Media Section */}
              {emergency.status === "Active" && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-[#363062]">
                      Media ({tempMedia.length}/10)
                    </h3>
                    <div className="flex items-center space-x-2">
                      <label className="cursor-pointer p-2 bg-[#363062] text-white rounded-md hover:bg-[#363062]/90 transition-colors">
                        <input
                          type="file"
                          onChange={(e) => handleUpload(e, "media")}
                          className="hidden"
                          accept="image/*,video/*"
                          multiple
                        />
                        <FiUpload className="w-4 h-4" />
                      </label>
                    </div>
                  </div>

                  {/* Show upload progress */}
                  {activeUpload === "media" && uploadProgress > 0 && (
                    <div className="mb-3">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#8FCD00] transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        Uploading... {uploadProgress}%
                      </p>
                    </div>
                  )}

                  {tempMedia.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {tempMedia.map((media, index) => (
                        <div key={index} className="relative group">
                          {media.type === "image" ? (
                            <img
                              src={media.url}
                              alt={media.name}
                              className="w-full h-24 object-cover rounded-md cursor-pointer"
                              onClick={() => setSelectedMedia(media)}
                            />
                          ) : (
                            <video
                              src={media.url}
                              className="w-full h-24 object-cover rounded-md cursor-pointer"
                              onClick={() => setSelectedMedia(media)}
                            />
                          )}
                          <div className="absolute top-1 right-1 flex space-x-1">
                            <button
                              onClick={() => setShowDeleteConfirm(index)}
                              className="p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {showDeleteConfirm === index && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md">
                              <div className="bg-white p-3 rounded-lg shadow-lg text-center">
                                <FiAlertCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                                <p className="text-sm mb-2">
                                  Delete this {media.type}?
                                </p>
                                <div className="flex space-x-2 justify-center">
                                  <button
                                    onClick={() => handleDeleteMedia(index)}
                                    className="px-3 py-1 bg-red-500 text-white rounded-md text-sm hover:bg-red-600"
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions Taken Section */}
              <div>
                <h3 className="font-semibold text-gray-900">Actions Taken</h3>
                {emergency.status === "Active" && (
                  <div className="flex mt-2">
                    <input
                      type="text"
                      value={newAction}
                      onChange={(e) => setNewAction(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#363062] focus:ring-[#363062] sm:text-sm"
                      placeholder="Add an action"
                    />
                    <button
                      type="button"
                      onClick={addNewAction}
                      className="ml-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#363062] hover:bg-[#363062]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#363062]"
                    >
                      Add
                    </button>
                  </div>
                )}
                <div className="mt-2 space-y-2">
                  {emergency.actions?.map((action, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-md">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-gray-700">{action.action}</p>
                      </div>
                      {action.media && action.media.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {action.media.map((media, mediaIndex) => (
                            <div key={mediaIndex} className="relative group">
                              {media.type === "image" ? (
                                <img
                                  src={media.url}
                                  alt={`Action ${index + 1} media ${
                                    mediaIndex + 1
                                  }`}
                                  className="w-20 h-20 object-cover rounded-md cursor-pointer"
                                  onClick={() => openMediaPreview(media)}
                                />
                              ) : (
                                <video
                                  src={media.url}
                                  className="w-20 h-20 object-cover rounded-md cursor-pointer"
                                  onClick={() => openMediaPreview(media)}
                                />
                              )}
                              <button
                                onClick={() => openMediaPreview(media)}
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity"
                              >
                                <FiMaximize2 className="text-white opacity-0 group-hover:opacity-100" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <span>{action.addedBy}</span>
                        <span className="mx-1">•</span>
                        <span>
                          {new Date(action.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Resolution Notes Section */}
              {emergency.status === "Active" && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Resolution Notes
                  </h3>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Describe how the emergency was resolved..."
                    rows={3}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#363062] focus:ring-[#363062] sm:text-sm"
                  />
                  <button
                    onClick={resolveEmergency}
                    className="mt-2 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm bg-[#8FCD00] hover:bg-[#8FCD00]/90"
                  >
                    Mark as Resolved
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditSave}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                emergency.status === "Active" && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  >
                    Edit Details
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Media Preview Modal */}
      {selectedMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4">
          <button
            onClick={closeMediaPreview}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <FiX className="w-6 h-6" />
          </button>
          <div className="max-w-4xl max-h-[90vh]">
            {selectedMedia.type === "image" ? (
              <img
                src={selectedMedia.url}
                alt="Preview"
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : (
              <video
                src={selectedMedia.url}
                controls
                className="max-w-full max-h-[90vh]"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default EmergencyModal;
